import React, { useState, useEffect, useMemo } from 'react'
import { SearchBar, LoadingSpinner } from '../utils'
import { useDocumento } from '../../hooks/useDocumento'
import { BibliotecaFile } from '../../types/strapi'
import { buildFileUrl } from '../../config/strapi'

// Interfaces para tipagem
interface DocumentoSection {
  id: number
  title: string
  File?: BibliotecaFile[]
}

export function DocumentosPage() {
  const { data, loading, error, refetch } = useDocumento()
  const [searchTerm, setSearchTerm] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  const [isSmallMobile, setIsSmallMobile] = useState(false)

  // Hook para detectar tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      setIsSmallMobile(window.innerWidth <= 480)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Filtra os arquivos baseado no termo de pesquisa
  const filteredSections = useMemo(() => {
    if (!data?.documentoList) return []
    
    if (!searchTerm.trim()) return data.documentoList

    return data.documentoList.map((section: DocumentoSection) => {
      const sectionTitle = section.title?.toLowerCase() || ''
      const searchLower = searchTerm.toLowerCase()
      
      // Verifica se o título da seção contém o termo de pesquisa
      const sectionMatches = sectionTitle.includes(searchLower)
      
      // Filtra os arquivos que contêm o termo
      const filteredFiles = (section.File || []).filter((file: BibliotecaFile) => {
        const fileTitle = file.titleArquivo?.toLowerCase() || ''
        const fileDescription = file.descriptionArquivo?.toLowerCase() || ''
        return fileTitle.includes(searchLower) || fileDescription.includes(searchLower)
      })
      
      // Retorna a seção apenas se ela ou algum arquivo contém o termo
      if (sectionMatches || filteredFiles.length > 0) {
        return {
          ...section,
          File: sectionMatches ? section.File : filteredFiles // Se a seção não contém o termo, mostra apenas os arquivos filtrados
        }
      }
      
      return null
    }).filter((section: DocumentoSection | null): section is DocumentoSection => section !== null) // Remove seções nulas com type guard
  }, [data, searchTerm])

  const clearSearch = () => {
    setSearchTerm('')
  }

  const hasActiveFilters = searchTerm.trim().length > 0

  const handleFileAction = (file: BibliotecaFile) => {
    // Verifica se tem link
    if (file.link && file.link.trim()) {
      // Abre o link em nova aba
      window.open(file.link, '_blank')
      return
    }
    
    // Verifica se tem arquivo
    if (file.arquivo && file.arquivo.url) {
      // Download do arquivo
      const link = document.createElement('a')
      link.href = buildFileUrl(file.arquivo.url)
      link.download = file.titleArquivo
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return
    }
    
    // Se não tem nem link nem arquivo, mostra um alerta
  }

  if (loading) {
    return <LoadingSpinner message="Carregando documentos..." />
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        background: '#EFF4F6',
        color: '#333'
      }}>
        <h2>Erro ao carregar documentos</h2>
        <p>{error}</p>
        <button
          onClick={refetch}
          style={{
            padding: '10px 20px',
            backgroundColor: '#0072BB',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Tentar Novamente
        </button>
      </div>
    )
  }

  return (
    <div style={{ 
      background: '#EFF4F6',
      color: '#333',
      fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      letterSpacing: 0.3
    }}>
      <div style={{
        padding: '20px 20px 3rem',
        maxWidth: 1200,
        margin: '0 auto'
      }}>
        {/* Campo de Busca */}
        <SearchBar
          placeholder="Buscar documentos por título, descrição ou categoria..."
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={clearSearch}
          showResultsCount={hasActiveFilters}
          resultsCount={filteredSections.reduce((total: number, section: DocumentoSection) => total + (section.File?.length || 0), 0)}
          resultsLabel={filteredSections.reduce((total: number, section: DocumentoSection) => total + (section.File?.length || 0), 0) === 1 ? 'documento encontrado' : 'documentos encontrados'}
        />

        {/* Seções de Arquivos */}
        {filteredSections.length > 0 ? (
          filteredSections.map((section: DocumentoSection) => (
            <div key={section.id} style={{ marginBottom: '3rem' }}>
              {/* Título da Seção */}
              <h2 style={{
                fontSize: '1.8rem',
                fontWeight: '700',
                color: '#0072BB',
                textAlign: 'left',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}>
                {section.title}
              </h2>

              {/* Verifica se há arquivos na seção */}
              {section.File && section.File.length > 0 ? (
                /* Grid de cards de arquivos */
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  {section.File.map((file: BibliotecaFile) => (
                    <div
                      key={file.id}
                      style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                      onClick={() => handleFileAction(file)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)'
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      <div>
                        <h3 style={{
                          fontSize: isSmallMobile ? '1rem' : isMobile ? '1.1rem' : '1.2rem',
                          fontWeight: '600',
                          color: '#0072BB',
                          margin: '0 0 0.5rem 0',
                          lineHeight: 1.4,
                          textAlign: 'left',
                          fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        }}>
                          {file.titleArquivo}
                        </h3>
                        
                        <p style={{
                          fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : '1.1rem',
                          color: '#666',
                          margin: 0,
                          lineHeight: 1.5,
                          textAlign: 'left',
                          fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        }}>
                          {file.descriptionArquivo}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Mensagem quando não há arquivos na seção */
                <div style={{
                  textAlign: 'center',
                  padding: isSmallMobile ? '1.5rem 1rem' : isMobile ? '2rem 1rem' : '3rem 1rem',
                  color: '#666',
                  fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : '1.1rem',
                  background: 'white',
                  borderRadius: '8px',
                  marginBottom: '2rem'
                }}>
                  <p>Nenhum documento disponível nesta seção</p>
                </div>
              )}
            </div>
          ))
        ) : (
          <div style={{
            textAlign: 'center',
            padding: isSmallMobile ? '1.5rem 1rem' : isMobile ? '2rem 1rem' : '3rem 1rem',
            color: '#666',
            fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : '1.1rem'
          }}>
            {hasActiveFilters ? (
              <>
                <p>Nenhum documento encontrado para "{searchTerm}"</p>
                <button
                  onClick={clearSearch}
                  style={{
                    marginTop: '1rem',
                    padding: isSmallMobile ? '8px 16px' : '10px 20px',
                    backgroundColor: '#0072BB',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: isSmallMobile ? '0.9rem' : '1rem'
                  }}
                >
                  Limpar busca
                </button>
              </>
            ) : (
              <p>Nenhum documento disponível</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
} 